import { CrudForm } from '../shared/CrudPage';
import { menuService } from '@kss/api';
import type { Menu, MenuCreate, MenuFilter } from '@kss/api';
import { loadMenuOptions } from '../shared/formLoaders';

/**
 * Live `Menu` API rejects `path` with "unknown field path in request body"
 * — the column is named `route` server-side. We surface the same input
 * label ("Path") to the user but write to `route` on save. The
 * `fromEntity` falls back to `r.path` for legacy rows so existing menus
 * still hydrate the form.
 *
 * Bug ref: AI Agent (round 3) — Menu save fails "unknown field path".
 */
type F = { name: string; icon: string; route: string; parentId: string; sortOrder: number; isActive: boolean };
const empty: F = { name: '', icon: '', route: '', parentId: '', sortOrder: 0, isActive: true };

export default function NewMenu() {
  return (
    <CrudForm<Menu, MenuCreate, MenuFilter, F>
      title="menu" service={menuService} idKey="menuId" basePath="/panel/menus" emptyForm={empty}
      fields={[
        { key: 'name', label: 'Name', required: true, placeholder: 'Dashboard' },
        { key: 'icon', label: 'Icon', placeholder: 'LayoutDashboard' },
        { key: 'route', label: 'Path', placeholder: '/panel/dashboard' },
        { key: 'parentId', label: 'Parent menu', type: 'search', loadOptions: loadMenuOptions, placeholder: 'Pick parent (optional)' },
        { key: 'sortOrder', label: 'Sort order', type: 'number' },
        { key: 'isActive', label: 'Active', type: 'status' },
      ]}
      fromEntity={(r) => ({
        name: r.name ?? '',
        icon: r.icon ?? '',
        /* New `route` field on the form; falls back to legacy `path`
           when hydrating older rows. */
        route: (r as { route?: string }).route ?? r.path ?? '',
        parentId: r.parentId ?? '',
        sortOrder: Number(r.sortOrder ?? 0),
        isActive: !!r.isActive,
      })}
      toEntity={(f, id) => ({
        menuId: id,
        name: f.name,
        icon: f.icon || undefined,
        /* Send as `route` to match the live API field name. */
        route: f.route || undefined,
        parentId: f.parentId || undefined,
        sortOrder: f.sortOrder || undefined,
        isActive: f.isActive,
      })}
      validate={(f) => !f.name.trim() ? 'Name is required' : null}
    />
  );
}
